import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { SubmissionPayPal } from '../components/SubmissionPayPal';
import { 
  Upload, Plus, Trash2, Check, Image, AlertCircle, 
  ArrowLeft, ArrowRight, Loader2, CalendarDays, X, CreditCard
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB

export const SubmissionForm = () => {
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const editSubmissionId = searchParams.get('edit');
  const isEditMode = !!editSubmissionId;
  
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [artworks, setArtworks] = useState([
    { title: '', size: '', medium: '', medium_category_id: '', price: '', image_urls: [], uploading: false }
  ]);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const fileInputRefs = useRef({});

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchCall = useCallback(async () => {
    if (!token) return;
    try {
      if (isEditMode && editSubmissionId) {
        // EDIT MODE: Fetch submission first — it embeds the call data
        const subRes = await fetch(`${API_URL}/api/submissions/my/${editSubmissionId}`, { headers });
        if (!subRes.ok) {
          const status = subRes.status;
          toast.error(status === 403 ? 'You do not have permission to edit this submission' : 'Could not load submission for editing');
          navigate('/members/submissions');
          return;
        }
        const subData = await subRes.json();

        // Populate artworks from the submission
        if (subData.artworks && subData.artworks.length > 0) {
          setArtworks(subData.artworks.map(aw => ({
            title: aw.title || '',
            size: aw.size || '',
            medium: aw.medium || '',
            medium_category_id: aw.medium_category_id || '',
            price: aw.price?.toString() || '',
            image_urls: aw.image_urls || [],
            uploading: false
          })));
        }

        // Use call embedded in submission; fallback to explicit fetch only if missing
        let callData = subData.call;
        if (!callData) {
          const fallbackId = subData.call_id || callId;
          if (fallbackId) {
            const callRes = await fetch(`${API_URL}/api/exhibitions/calls/${fallbackId}`, { headers });
            if (callRes.ok) callData = await callRes.json();
          }
        }

        if (!callData) throw new Error('Call not found');
        setCall(callData);

        // Load judging categories if available
        const exhibitionId = callData.exhibition_id || subData.exhibition_id;
        if (exhibitionId) {
          const catRes = await fetch(`${API_URL}/api/judging/categories/public/${exhibitionId}`);
          if (catRes.ok) setCategories(await catRes.json());
        }
      } else {
        // NEW SUBMISSION MODE: Load call by callId from route param
        if (!callId) return;
        const res = await fetch(`${API_URL}/api/exhibitions/calls/${callId}`, { headers });
        if (!res.ok) throw new Error('Call not found');
        const data = await res.json();
        setCall(data);

        if (data.exhibition_id) {
          const catRes = await fetch(`${API_URL}/api/judging/categories/public/${data.exhibition_id}`);
          if (catRes.ok) setCategories(await catRes.json());
        }
      }
    } catch (e) {
      toast.error('Could not load submission details');
      navigate('/members/submissions');
    } finally {
      setLoading(false);
    }
  }, [token, callId, editSubmissionId, isEditMode]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/members/login');
      return;
    }
    fetchCall();
  }, [authLoading, isAuthenticated, fetchCall, navigate]);

  const handleImageUpload = async (artworkIndex, files) => {
    const fileArr = Array.from(files);
    const current = artworks[artworkIndex];
    
    for (const file of fileArr) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`"${file.name}" is not a supported image type. Please use JPEG, PNG, or WebP.`);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        toast.error(`"${file.name}" is ${mb}MB — maximum is 3MB. Please resize the image.`);
        return;
      }
    }
    
    const updated = [...artworks];
    updated[artworkIndex] = { ...current, uploading: true };
    setArtworks(updated);
    
    const newUrls = [...current.image_urls];
    
    for (const file of fileArr) {
      const fd = new FormData();
      fd.append('file', file);
      
      try {
        const res = await fetch(`${API_URL}/api/submissions/upload-image`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
        });
        if (!res.ok) {
          const err = await res.json();
          toast.error(err.detail || 'Upload failed');
          continue;
        }
        const data = await res.json();
        newUrls.push(data.url);
      } catch (e) {
        toast.error(`Failed to upload "${file.name}"`);
      }
    }
    
    const final = [...artworks];
    final[artworkIndex] = { ...current, image_urls: newUrls, uploading: false };
    setArtworks(final);
  };

  const removeImage = (artworkIndex, imageIndex) => {
    const updated = [...artworks];
    updated[artworkIndex].image_urls.splice(imageIndex, 1);
    setArtworks(updated);
  };

  const addArtwork = () => {
    if (artworks.length >= (call?.max_artworks || 5)) {
      toast.error(`Maximum ${call?.max_artworks || 5} artworks allowed`);
      return;
    }
    setArtworks([...artworks, { title: '', size: '', medium: '', medium_category_id: '', price: '', image_urls: [], uploading: false }]);
  };

  const removeArtwork = (index) => {
    if (artworks.length <= 1) return;
    setArtworks(artworks.filter((_, i) => i !== index));
  };

  const updateArtwork = (index, field, value) => {
    const updated = [...artworks];
    updated[index] = { ...updated[index], [field]: value };
    setArtworks(updated);
  };

  const validateAndReview = () => {
    for (let i = 0; i < artworks.length; i++) {
      const aw = artworks[i];
      if (!aw.title.trim()) { toast.error(`Artwork ${i+1}: Please enter a title`); return; }
      if (!aw.size.trim()) { toast.error(`Artwork ${i+1}: Please enter the size`); return; }
      if (!aw.medium.trim()) { toast.error(`Artwork ${i+1}: Please enter the medium`); return; }
      if (categories.length > 0 && !aw.medium_category_id) { toast.error(`Artwork ${i+1}: Please select a medium category`); return; }
      if (!aw.price && aw.price !== 0) { toast.error(`Artwork ${i+1}: Please enter the price`); return; }
      if (aw.image_urls.length === 0) { toast.error(`Artwork ${i+1}: Please upload at least one image`); return; }
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        artworks: artworks.map(aw => ({
          title: aw.title.trim(),
          size: aw.size.trim(),
          medium: aw.medium.trim(),
          medium_category_id: aw.medium_category_id || '',
          price: parseFloat(aw.price),
          image_urls: aw.image_urls
        }))
      };
      
      let res;
      if (isEditMode) {
        res = await fetch(`${API_URL}/api/submissions/edit/${editSubmissionId}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        payload.call_id = callId;
        res = await fetch(`${API_URL}/api/submissions/submit-json`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Submission failed');
      }
      
      const result = await res.json();
      
      // Check if payment is required
      if (result.payment_required && result.submission) {
        setPendingSubmission(result.submission);
        setStep(3); // Payment step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setStep(4); // Success
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalSuccess = async (submission) => {
    try {
      // Create PayPal order
      const orderRes = await fetch(`${API_URL}/api/submissions/pay/${submission.id}`, {
        method: 'POST', headers
      });
      if (!orderRes.ok) throw new Error('Failed to create payment order');
      return (await orderRes.json()).order_id;
    } catch (e) {
      toast.error(e.message);
      throw e;
    }
  };

  const capturePayment = async (orderId, capabilityToken) => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/pay/${pendingSubmission.id}/capture/${orderId}`, {
        method: 'POST', headers: { ...headers, 'X-Payment-Capability': capabilityToken }
      });
      if (!res.ok) throw new Error('Payment capture failed');
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div></div>;
  }

  if (!call) return null;

  const isClosed = !call.is_open && !isEditMode;
  const deadline = new Date(call.deadline);

  // SUCCESS STATE
  if (step === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white" data-testid="submission-success">
        <SEOHead title={isEditMode ? "Submission Updated | CAFA" : "Submission Received | CAFA"} />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif text-stone-900 mb-4">
            {isEditMode ? 'Submission Updated!' : 'Submission Received!'}
          </h1>
          <p className="text-lg text-stone-600 mb-2">
            {isEditMode ? 'Your changes have been saved for' : 'Thank you for submitting your artwork to'}
          </p>
          <p className="text-xl font-semibold text-stone-900 mb-6">{call.exhibition?.title}</p>
          {pendingSubmission?.total_fees > 0 && (
            <div className="inline-block bg-green-50 border border-green-200 rounded-lg px-6 py-3 mb-6">
              <p className="text-green-800 font-medium">Payment confirmed: ${pendingSubmission.total_fees.toFixed(2)}</p>
            </div>
          )}
          <p className="text-stone-500 mb-8">
            {isEditMode 
              ? 'Your submission has been updated.'
              : 'A confirmation email has been sent to your email address. You will be notified when your submission has been reviewed.'
            }
          </p>
          <Button size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white text-lg px-8" onClick={() => navigate('/members/submissions')}>
            Back to Submissions
          </Button>
        </div>
      </div>
    );
  }

  // CLOSED STATE (only for new submissions, not edits)
  if (isClosed) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="submissions-closed">
        <SEOHead title="Submissions Closed | CAFA" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-stone-400" />
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Submissions Closed</h1>
          <p className="text-lg text-stone-600 mb-6">The deadline for this call has passed.</p>
          <Button variant="outline" onClick={() => navigate('/members/submissions')}>Back to Submissions</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="submission-form-page">
      <SEOHead title={isEditMode ? "Edit Submission | CAFA" : "Submit Artwork | CAFA"} />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" className="mb-4 text-stone-500" onClick={() => step === 1 ? navigate('/members/submissions') : setStep(1)} data-testid="back-btn">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <h1 className="text-3xl sm:text-4xl font-serif text-stone-900">
            {isEditMode ? 'Edit Submission' : 'Submit Artwork'}
          </h1>
          <p className="text-lg text-stone-600 mt-2">{call.exhibition?.title}</p>
          <div className="flex items-center gap-2 mt-3 text-stone-700">
            <CalendarDays size={18} className="text-cafa-red" />
            <span className="text-base font-medium">
              Deadline: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          {isEditMode && (
            <Badge className="mt-3 bg-amber-100 text-amber-800 text-sm" data-testid="edit-mode-badge">Editing existing submission</Badge>
          )}
          {call.instructions && <p className="text-stone-500 mt-3">{call.instructions}</p>}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-10" data-testid="progress-steps">
          {(() => {
            const hasFees = call?.fees_enabled && ((call?.submission_fee || 0) + (call?.per_artwork_fee || 0) * artworks.length) > 0 && !isEditMode;
            const steps = hasFees 
              ? [{n:1,l:'Add Artwork'},{n:2,l:'Review'},{n:3,l:'Payment'}]
              : [{n:1,l:'Add Artwork'},{n:2,l: isEditMode ? 'Review & Save' : 'Review & Submit'}];
            return steps.map(({n,l}, i) => (
              <React.Fragment key={n}>
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${step >= n ? 'bg-cafa-red text-white' : 'bg-stone-200 text-stone-500'}`}>{n}</div>
                  <span className={`text-sm font-medium ${step >= n ? 'text-stone-900' : 'text-stone-400'}`}>{l}</span>
                </div>
                {i < steps.length - 1 && <div className={`w-12 h-0.5 ${step > n ? 'bg-cafa-red' : 'bg-stone-200'}`} />}
              </React.Fragment>
            ));
          })()}
        </div>

        {/* STEP 1: Add Artworks */}
        {step === 1 && (
          <div className="space-y-6">
            {artworks.map((aw, i) => (
              <Card key={i} className="border-stone-200 shadow-sm" data-testid={`artwork-block-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-serif text-stone-900">Artwork {i + 1}</h3>
                    {artworks.length > 1 && (
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeArtwork(i)} data-testid={`remove-artwork-${i}`}>
                        <Trash2 size={16} className="mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Title *</Label>
                      <Input className="text-lg h-12 mt-1" placeholder="e.g. Morning Light" value={aw.title}
                        onChange={e => updateArtwork(i, 'title', e.target.value)} data-testid={`artwork-title-${i}`} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-base font-medium">Size *</Label>
                        <Input className="text-lg h-12 mt-1" placeholder='e.g. 16"x20"' value={aw.size}
                          onChange={e => updateArtwork(i, 'size', e.target.value)} data-testid={`artwork-size-${i}`} />
                      </div>
                      <div>
                        <Label className="text-base font-medium">Medium *</Label>
                        <Input className="text-lg h-12 mt-1" placeholder="e.g. Oil on canvas" value={aw.medium}
                          onChange={e => updateArtwork(i, 'medium', e.target.value)} data-testid={`artwork-medium-${i}`} />
                      </div>
                      <div>
                        <Label className="text-base font-medium">Price ($) *</Label>
                        <Input className="text-lg h-12 mt-1" type="number" min="0" step="1" placeholder="e.g. 500" value={aw.price}
                          onChange={e => updateArtwork(i, 'price', e.target.value)} data-testid={`artwork-price-${i}`} />
                      </div>
                    </div>
                    {categories.length > 0 && (
                      <div>
                        <Label className="text-base font-medium">Medium Category *</Label>
                        <select className="w-full text-lg h-12 mt-1 border rounded-md px-3 bg-white"
                          value={aw.medium_category_id}
                          onChange={e => updateArtwork(i, 'medium_category_id', e.target.value)}
                          data-testid={`artwork-category-${i}`}>
                          <option value="">Select a category...</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Image Upload Area */}
                    <div>
                      <Label className="text-base font-medium mb-2 block">Images *</Label>
                      
                      {aw.image_urls.length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {aw.image_urls.map((url, j) => (
                            <div key={j} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-stone-200 group">
                              <img src={url} alt={`Upload ${j+1}`} className="w-full h-full object-cover" />
                              <button onClick={() => removeImage(i, j)}
                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`remove-image-${i}-${j}`}>
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                          ${aw.uploading ? 'border-cafa-red bg-red-50' : 'border-stone-300 hover:border-cafa-red hover:bg-red-50/50'}`}
                        onClick={() => fileInputRefs.current[i]?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-cafa-red', 'bg-red-50'); }}
                        onDragLeave={e => { e.currentTarget.classList.remove('border-cafa-red', 'bg-red-50'); }}
                        onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-cafa-red', 'bg-red-50'); handleImageUpload(i, e.dataTransfer.files); }}
                        data-testid={`upload-zone-${i}`}
                      >
                        <input ref={el => fileInputRefs.current[i] = el} type="file" multiple
                          accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={e => { handleImageUpload(i, e.target.files); e.target.value = ''; }}
                          data-testid={`file-input-${i}`} />
                        
                        {aw.uploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 size={32} className="animate-spin text-cafa-red" />
                            <p className="text-cafa-red font-medium">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload size={36} className="text-stone-400" />
                            <p className="text-lg font-medium text-stone-700">Drag & drop images here</p>
                            <p className="text-stone-500">or click to browse</p>
                            <p className="text-sm text-stone-400 mt-1">JPEG, PNG, or WebP &bull; Max 3MB each</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {artworks.length < (call?.max_artworks || 5) && (
              <Button variant="outline" size="lg" className="w-full h-14 text-lg border-dashed border-2" onClick={addArtwork} data-testid="add-artwork-btn">
                <Plus size={20} className="mr-2" /> Add Another Artwork
              </Button>
            )}
            <p className="text-center text-stone-400 text-sm">{artworks.length} of {call?.max_artworks || 5} artworks</p>

            <Button size="lg" className="w-full h-14 text-lg bg-cafa-red hover:bg-cafa-red-900 text-white" onClick={validateAndReview} data-testid="continue-to-review">
              Continue to Review <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {/* STEP 2: Review */}
        {step === 2 && (
          <div className="space-y-6">
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <h2 className="text-2xl font-serif text-stone-900 mb-6">
                  {isEditMode ? 'Review Your Changes' : 'Review Your Submission'}
                </h2>
                
                <div className="space-y-6">
                  {artworks.map((aw, i) => (
                    <div key={i} className="border rounded-lg p-4" data-testid={`review-artwork-${i}`}>
                      <h3 className="text-lg font-semibold mb-2">{aw.title}</h3>
                      <div className="grid grid-cols-3 gap-3 text-sm text-stone-600 mb-3">
                        <div><span className="text-stone-400">Size:</span> {aw.size}</div>
                        <div><span className="text-stone-400">Medium:</span> {aw.medium}</div>
                        <div><span className="text-stone-400">Price:</span> ${aw.price}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {aw.image_urls.map((url, j) => (
                          <img key={j} src={url} alt={`${aw.title} ${j+1}`} className="w-20 h-20 object-cover rounded-lg border" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {!isEditMode && call.fees_enabled && (
                  <div className="bg-stone-50 rounded-lg p-4 mt-6" data-testid="fee-summary">
                    <h4 className="font-medium mb-2">Fee Summary</h4>
                    {call.submission_fee > 0 && <p>Submission fee: ${call.submission_fee.toFixed(2)}</p>}
                    {call.per_artwork_fee > 0 && <p>Per artwork fee ({artworks.length} x ${call.per_artwork_fee.toFixed(2)}): ${(artworks.length * call.per_artwork_fee).toFixed(2)}</p>}
                    <p className="font-bold mt-2">Total: ${((call.submission_fee || 0) + (call.per_artwork_fee || 0) * artworks.length).toFixed(2)}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex gap-4">
              <Button variant="outline" size="lg" className="flex-1 h-14 text-lg" onClick={() => setStep(1)} data-testid="back-to-edit">
                <ArrowLeft size={18} className="mr-2" /> Edit Artworks
              </Button>
              <Button size="lg" className="flex-1 h-14 text-lg bg-cafa-red hover:bg-cafa-red-900 text-white" onClick={handleSubmit} disabled={submitting} data-testid="final-submit-btn">
                {submitting 
                  ? <><Loader2 size={20} className="mr-2 animate-spin" /> Saving...</>
                  : isEditMode 
                    ? <><Check size={20} className="mr-2" /> Save Changes</>
                    : (call?.fees_enabled && ((call?.submission_fee || 0) + (call?.per_artwork_fee || 0) * artworks.length) > 0)
                      ? <><CreditCard size={20} className="mr-2" /> Continue to Payment</>
                      : <><Check size={20} className="mr-2" /> Submit Artwork</>
                }
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Payment (only when fees enabled) */}
        {step === 3 && pendingSubmission && (
          <div className="space-y-6" data-testid="payment-step">
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <CreditCard size={40} className="mx-auto text-cafa-red mb-3" />
                  <h2 className="text-2xl font-serif text-stone-900">Payment Required</h2>
                  <p className="text-stone-600 mt-1">Complete payment to finalize your submission</p>
                </div>
                
                {/* Fee Breakdown */}
                <div className="bg-stone-50 rounded-lg p-5 mb-6" data-testid="fee-breakdown">
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">Fee Summary</h3>
                  <div className="space-y-2">
                    {call.submission_fee > 0 && (
                      <div className="flex justify-between text-stone-700">
                        <span>Submission fee</span>
                        <span>${call.submission_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {call.per_artwork_fee > 0 && (
                      <div className="flex justify-between text-stone-700">
                        <span>Per artwork fee ({pendingSubmission.total_artworks} x ${call.per_artwork_fee.toFixed(2)})</span>
                        <span>${(pendingSubmission.total_artworks * call.per_artwork_fee).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-stone-200 pt-2 mt-2 flex justify-between font-semibold text-stone-900 text-lg">
                      <span>Total</span>
                      <span>${pendingSubmission.total_fees.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* PayPal Buttons */}
                <SubmissionPayPal
                  onCreateOrder={async () => {
                    const res = await fetch(`${API_URL}/api/submissions/pay/${pendingSubmission.id}`, {
                      method: 'POST', headers
                    });
                    if (!res.ok) throw new Error('Failed to create payment order');
                    const data = await res.json();
                    return data;
                  }}
                  onApprove={async (orderId, capabilityToken) => {
                    await capturePayment(orderId, capabilityToken);
                  }}
                  onError={() => {
                    toast.error('Payment was cancelled or failed. You can try again.');
                  }}
                />
                
                <p className="text-center text-stone-400 text-xs mt-4">
                  Your submission will be finalized after payment is confirmed.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionForm;
